import { useEffect, useState } from "react";
// @mui
import { styled } from "@mui/material/styles";
import {
    Slide,
    ClickAwayListener,
    Autocomplete,
    Box,
    Paper,
    PaperProps,
    useTheme,
    useMediaQuery,
    Popper,
    PopperProps,
} from "@mui/material";
// utils
import cssStyles from "../../../utils/cssStyles";
// components
import Iconify from "../../../components/Iconify";
import { useSearchBarAutocomplete } from "src/modules/dashboard/hooks/useSearchBarAutocomplete";
import useDebounce from "src/hooks/useDebounce";
import { useNavigate } from "react-router";
import {
    PATH_CUSTOMERS,
    PATH_LOCATIONS,
    PATH_STATIONS,
    PATH_TARIFFS,
    PATH_USERS,
} from "src/routes/paths";
import Label from "src/components/Label";
import { IconButtonAnimate } from "src/components/animate";
import { useTranslation } from "react-i18next";

const APPBAR_MOBILE = 64;
const APPBAR_DESKTOP = 92;

/**
 * t("shared.search.typelocation")
 * t("shared.search.typecompanyuser")
 * t("shared.search.typecompanycustomer")
 * t("shared.search.typetariff")
 * t("shared.search.typestation")
 */
const SearchbarStyle = styled("div")(({ theme }) => ({
    ...cssStyles(theme).bgBlur(),
    top: 0,
    left: 0,
    zIndex: 99,
    width: "100%",
    display: "flex",
    position: "absolute",
    alignItems: "center",
    height: APPBAR_MOBILE,
    padding: theme.spacing(0, 1, 0, 2),
    boxShadow: theme.customShadows.z8,
    [theme.breakpoints.up("md")]: {
        height: APPBAR_DESKTOP,
        padding: theme.spacing(0, 5),
    },
}));

const SearchbarContainerStyle = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    paddingRight: theme.spacing(8),
}));

const IconStyle = styled("div")(({ theme }) => ({
    width: 44,
    height: 44,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#847979",
    [theme.breakpoints.up("md")]: {},
}));

const TextFieldStyle = styled("div")(({ theme }) => ({
    "& > input": {
        width: "100%",
        border: 0,
        outline: 0,
        backgroundColor: "transparent",
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.pxToRem(20),
        fontWeight: theme.typography.fontWeightMedium,
        lineHeight: 1,
        padding: theme.spacing(2, 0, 2, 3),
        [theme.breakpoints.down("lg")]: {
            padding: theme.spacing(2, 0),
        },
    },
}));

const PaperComponent = (props: PaperProps) => {
    return <Paper sx={{ width: { xs: "100%", lg: 400 } }} {...props} />;
};

export default function Searchbar() {
    const theme = useTheme();
    const { t } = useTranslation();
    const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
    const [isOpen, setOpen] = useState(false);
    const navigate = useNavigate();

    const [searchPhrase, setSearchPhrase] = useState<string>("");
    const debouncedSearchPhrase = useDebounce(searchPhrase, 500);
    const {
        data: searchResults,
        refetch,
        isLoading,
    } = useSearchBarAutocomplete(debouncedSearchPhrase);

    useEffect(() => {
        refetch();
    }, [debouncedSearchPhrase]);

    const renderAutocomplete = (
        <Autocomplete
            autoFocus
            fullWidth
            value={null}
            inputValue={searchPhrase}
            PopperComponent={(props: PopperProps) => {
                return !isLoading && searchResults ? (
                    <Popper sx={{ width: { xs: "100%", lg: 400 } }} {...props} />
                ) : (
                    <></>
                );
            }}
            isOptionEqualToValue={(option, value) => {
                return option.id === value.id;
            }}
            blurOnSelect={true}
            getOptionLabel={(option) => `${option.name}`}
            renderOption={(props, option) => {
                return (
                    <Box
                        component="li"
                        sx={{
                            borderRadius: 0,
                            display: "grid",
                            gap: 2,
                        }}
                        {...props}
                    >
                        <Box
                            sx={{
                                width: 90,
                                display: "flex",
                                justifyContent: "flex-end",
                                pointerEvents: "none",
                            }}
                        >
                            <Label
                                variant="outlined"
                                sx={{
                                    textTransform: "none",
                                    color: "#A79870",
                                    borderColor: "#A79870",
                                }}
                            >
                                {t("shared.search.type" + option.type)}
                            </Label>
                        </Box>
                        {option.name}
                    </Box>
                );
            }}
            onInputChange={(e, value) => {
                setSearchPhrase(value);
            }}
            onChange={(e, v) => {
                if (v) {
                    switch (v.type) {
                        case "location":
                            navigate(PATH_LOCATIONS + `?id=${v.id}`);
                            break;
                        case "companyuser":
                            navigate(PATH_USERS + `?id=${v.id}`);
                            break;
                        case "companycustomer":
                            navigate(PATH_CUSTOMERS + `?id=${v.id}`);
                            break;
                        case "station":
                            navigate(PATH_STATIONS + `?id=${v.id}`);
                            break;
                        case "tariff":
                            navigate(PATH_TARIFFS + `?id=${v.id}`);
                            break;
                    }
                }
                setSearchPhrase("");
                handleClose();
                refetch();
            }}
            options={
                searchResults
                    ? searchResults.data.map((sr) => ({
                          id: sr.id,
                          name: sr.content,
                          type: sr.type,
                      }))
                    : []
            }
            renderInput={(params) => (
                <TextFieldStyle ref={params.InputProps.ref}>
                    <input
                        type="text"
                        // placeholder={`${t("layout.header.search")}...`}
                        {...params.inputProps}
                    />
                </TextFieldStyle>
            )}
            ListboxProps={{
                sx: {
                    padding: 0,
                    paddingTop: 1,
                    paddingBottom: 1,
                    "& .MuiAutocomplete-option": {
                        padding: 1,
                        margin: 0,
                        borderRadius: 0,
                    },
                },
            }}
            PaperComponent={PaperComponent}
        />
    );

    const handleOpen = () => {
        setOpen((prev) => !prev);
    };

    const handleClose = () => {
        setOpen(false);
    };

    return (
        <ClickAwayListener onClickAway={handleClose}>
            {!isMobile ? (
                <Box sx={{ width: "100%" }}>
                    <SearchbarContainerStyle>
                        <IconStyle>
                            <Iconify icon={"eva:search-fill"} width={24} height={24} />
                        </IconStyle>
                        {renderAutocomplete}
                    </SearchbarContainerStyle>
                </Box>
            ) : (
                <>
                    {!isOpen && (
                        <IconButtonAnimate onClick={handleOpen}>
                            <Iconify icon={"eva:search-fill"} width={20} height={20} />
                        </IconButtonAnimate>
                    )}
                    <Slide direction="down" in={isOpen} mountOnEnter unmountOnExit>
                        <SearchbarStyle>
                            {renderAutocomplete}
                            <IconButtonAnimate onClick={handleOpen}>
                                <Iconify icon={"eva:close-fill"} width={30} height={30} />
                            </IconButtonAnimate>
                        </SearchbarStyle>
                    </Slide>
                </>
            )}
        </ClickAwayListener>
    );
}
